import type { ComposerReply, PersonalComposer } from "../../src/personal-composer-contract.ts";
import type { UnsentReply } from "./unsent-reply.ts";

/** The entire editor contents. A signature inside these words is never split or inferred. */
export type ComposerWords = {
  format: "text" | "html";
  plainText: string;
  html: string;
  fromDraft: number | null;
  /** Whether the editor holds the rep's own words beyond its signature (LEA-513). */
  authored?: boolean;
  /**
   * The rest of the whole reply: recipients, Reply all and the file box. Absent where this side does
   * not know it (an older server, or words kept in this browser); null where the server keeps none.
   */
  reply?: ComposerReply | null;
};

/** A modern local edit is marked even before its first server read supplies a known base. */
export type ComposerLocalBase = {
  composerManaged: true;
  composerRevision?: number;
  composerThreadVersion?: string;
};

export function composerWords(
  composer: PersonalComposer,
  reply?: ComposerReply | null,
): ComposerWords | null {
  const content = composer.content;
  return content
    ? {
        format: content.format,
        plainText: content.plainText,
        html: content.format === "html" ? content.html : "",
        fromDraft: content.kind === "opaque" ? content.fromDraft : null,
        ...(reply === undefined ? {} : { reply }),
      }
    : null;
}

/** Recipients or a file: something of the rep's even with no words. */
const replyHolds = (reply: ComposerReply | null | undefined): boolean =>
  !!reply && (reply.recipients !== null || reply.files !== null);

/**
 * The reply is compared only when both sides carry one. Words kept in this browser and an older
 * server's read carry none; comparing against that absence would count the words as changed and
 * save the box's preset over the draft's saved recipients.
 */
const sameReply = (a: ComposerWords, b: ComposerWords): boolean =>
  a.reply === undefined ||
  b.reply === undefined ||
  JSON.stringify(a.reply ?? null) === JSON.stringify(b.reply ?? null);

export const unsentWords = (reply: UnsentReply): ComposerWords => ({
  format: reply.format,
  plainText: reply.text,
  html: reply.format === "html" ? reply.html : "",
  fromDraft: reply.fromDraft ?? null,
});

export function sameComposerWords(a: ComposerWords | null, b: ComposerWords | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.format === b.format &&
    a.plainText === b.plainText &&
    (a.format === "text" || a.html === b.html) &&
    a.fromDraft === b.fromDraft &&
    sameReply(a, b)
  );
}

export const nonemptyComposerWords = (words: ComposerWords): ComposerWords | null =>
  words.plainText.trim() || replyHolds(words.reply) ? words : null;
